import * as PIXI from "pixi.js";
import {Container} from "../Core/Container";
import {AbstractGameState} from "./AbstractGameState";
import {ButtonMute} from "../GameObjects/ButtonMute";
import {TiledSprite} from "../GameObjects/TiledSprite";
import {Door} from "../GameObjects/Door";
import {KnockKnock} from "../GameObjects/KnockKnock";
import {Package} from "../GameObjects/Package";
import {Ease} from "../Core/Animatable";
import {GameManager} from "../Managers/GameManager";
import {Sprite} from "../Core/Sprite";
import {Poly} from "../Core/Shape";
import {ShelfItemAssembly, ShelfItemBagpipes, ShelfItemButton, ShelfItemCooking, ShelfItemMusic, ShelfItemPuzzle} from "../GameObjects/ShelfItemButton";
import {StateName} from "../Data/StateName";
import {ItemType, PackageModel} from "../Data/ItemModel";
import {AbstractHomeActivity} from "../GameObjects/AbstractHomeActivity";
import {Overlay} from "../GameObjects/Overlay";
import {FromVO, nextVO, playVO, ReactionVO, stopVO, voOptionsIdle} from "../Data/VoiceOver";
import {LunaPrompter} from "../GameObjects/LunaPrompter";
import {PubSubEvent} from "../Managers/PubSubEvent";
import {main} from "../Layout/HomeScreenLayout";
import {array, Callback} from "../Core/Reflection";
import {Button} from "../GameObjects/Button";
import {ButtonPackage} from "../GameObjects/ButtonPackage";
import {BaseButton} from "../Core/Button";
import {PackageContextMenu} from "../GameObjects/PackageContextMenu";
import {HomeScreenModel} from "../Data/HomeScreenModel";
import {HomeScreenActivities} from "../GameObjects/HomeScreenActivities";
import {LunaCharacter} from "../GameObjects/LunaCharacter";
import {Keyboard} from "../GameObjects/Keyboard";
import {Analytics} from "../Managers/Analytics";
import {AudioChannel} from "../Core/AudioChannel";
import {ArrayUtils} from "html-living-framework";
import {Environment} from "../Data/Environment";

let currentPackage: PackageModel | undefined;
let nextPackageOnStart = false;
const packageIdleVO = ["LetsGoLuna_005", "LetsGoLuna_019", "LetsGoLuna_018"];

export class HomeScreen extends AbstractGameState {

    public readonly main = this.addChild(new Container());
    public readonly roomContainer = this.main.addChild(new Container());
    public readonly roomRight = {x: 0, y: 0};
    public readonly background = this.roomContainer.addChild(new TiledSprite());
    public readonly luna = this.roomContainer.addChild(new LunaCharacter());

    public readonly shelvesContainer = this.roomContainer.addChild(new Container());
    public readonly shelves = array(() => this.shelvesContainer.addChild(new Sprite()));
    public readonly shelfItemsContainer = this.roomContainer.addChild(new Container());
    public readonly shelfItems: ShelfItemButton[] = [
        this.shelfItemsContainer.addChild(new ShelfItemAssembly(ItemType.Robot)),
        this.shelfItemsContainer.addChild(new ShelfItemAssembly(ItemType.Monster)),
        this.shelfItemsContainer.addChild(new ShelfItemPuzzle()),
        this.shelfItemsContainer.addChild(new ShelfItemMusic(ItemType.Glockenspiel)),
        this.shelfItemsContainer.addChild(new ShelfItemCooking(ItemType.Durian)),
        this.shelfItemsContainer.addChild(new ShelfItemCooking(ItemType.Pasta)),
        this.shelfItemsContainer.addChild(new ShelfItemCooking(ItemType.Delight)),
        this.shelfItemsContainer.addChild(new ShelfItemCooking(ItemType.Coconut)),
        this.shelfItemsContainer.addChild(new ShelfItemAssembly(ItemType.Movie)),
        this.shelfItemsContainer.addChild(new ShelfItemMusic(ItemType.Nyatiti)),
        this.shelfItemsContainer.addChild(new ShelfItemMusic(ItemType.Didgeridoo)),
        this.shelfItemsContainer.addChild(new ShelfItemBagpipes()),
    ];

    public readonly packageBox = this.roomContainer.addChild(new Package());
    public readonly packageInDoor = {x: 0, y: 0, width: 0, height: 0};
    public readonly packageInRoom = {x: 0, y: 0, width: 0, height: 0};
    public readonly packageOpen = {x: 0, y: 0, width: 0, height: 0};

    public readonly door = this.roomContainer.addChild(new Door());
    public readonly doorMask: number[] = [];
    public readonly knockKnock = this.roomContainer.addChild(new KnockKnock());
    public readonly buttonPackage = this.main.addChild(new ButtonPackage());
    public readonly overlay = this.main.addChild(new Overlay());
    public readonly activityContainer = this.main.addChild(new Container());
    public readonly buttonGo = this.main.addChild(new Button());
    public readonly lunaPrompter = this.main.addChild(new LunaPrompter());
    public readonly buttonMute = this.addChild(new ButtonMute());

    private takePackageTimeout?: number;
    private isPackageTaken?: boolean;
    private isPackageOpen?: boolean;
    private firstItemTimeout?: number;
    private isDoorOpen?: boolean;
    private postcardIdleInterval?: number;
    private isLastPackage = false;

    public init(pSize: PIXI.Point, pData?: HomeScreenModel) {
        super.init(pSize);

        if (nextPackageOnStart) {
            nextPackageOnStart = false;
            this.gameManager.nextPackage();
            this.isLastPackage = this.gameManager.allPackagesReceived;
        }

        main.call(this);
        this.main.scale.set(0.5);

        this.endActivityEvent(pData);

        this.luna.scale.set(0.8);
        this.luna.characterSpine.autoUpdate = false;
        this.luna.mask = this.roomContainer.addChild(new Poly(this.doorMask));

        this.packageBox.visible = false;
        this.packageBox.saveTransform();

        this.door.enabled = false;
        this.door.onClick.add(this.openDoor, this);

        this.buttonPackage.visible = false;
        this.buttonPackage.colorful = false;
        this.buttonPackage.onClick.add(this.onClickButtonPackage, this);
        this.buttonGo.visible = false;
        this.buttonGo.hoverVO = "lsf_ui10";

        for (const button of this.shelfItems) {
            const item = this.gameManager.getItem(button.itemType);
            if (item) {
                button.setItem(item);
            }
            button.onClick.add(() => {
                this.shelfItems.forEach(it => it.stopPulse());
                this.buttonPackage.stopPulse();
                window.clearTimeout(this.takePackageTimeout);
                window.clearTimeout(this.firstItemTimeout);
                stopVO();
                const item = this.gameManager.getItem(button.itemType);
                if (!item) {
                    return;
                }
                this.noneKeyboard();
                const editPackage = this.gameManager.getEditPackage(item);
                if (editPackage) {
                    Analytics.event("select_content", this.getActivityLabel(editPackage));
                    this.startActivityEvent(editPackage);
                }
                if (editPackage && editPackage.stateName === StateName.Home) {
                    const activity = this.getActivity(editPackage);
                    if (activity) {
                        this.activityContainer.addChild(activity);
                        activity.open(undefined, button, (model?: HomeScreenModel) => {
                            this.normalKeyboard();
                            this.endActivityEvent(model);
                        });
                    }
                } else if (editPackage) {
                    this.gotoState(editPackage.stateName, editPackage.itemData);
                }
            });
        }

        PubSubEvent.voiceOverStarted.add(() => {
            if (!this.isDoorOpen) {
                this.lunaPrompter.show();
            }
        }, this);
        PubSubEvent.voiceOverEnded.add(() => {
            this.lunaPrompter.hide();
        }, this);

        this.onReturnHome();
        this.normalKeyboard();
    }

    destroy(pOptions?: Parameters<typeof PIXI.Container.prototype.destroy>[0]) {
        PubSubEvent.voiceOverStarted.remove(this);
        PubSubEvent.voiceOverEnded.remove(this);
        window.clearTimeout(this.firstItemTimeout);
        window.clearTimeout(this.takePackageTimeout);
        window.clearInterval(this.postcardIdleInterval);
        GameManager.instance.onPackageArrived.remove(this);
        super.destroy(pOptions);
    }

    update(pDeltaTime: number) {
        super.update(pDeltaTime);
    }

    public onResize(pSize: PIXI.Point) {
        super.onResize(pSize);

        if (this.buttonMute) {
            this.buttonMute.setPosition(this._size);
        }
        this.overlay.width = pSize.x * 2;
        this.overlay.height = pSize.y * 2;
    }

    private onReturnHome() {
        switch (this.gameManager.visits) {
            case 0:
                this.onFirstVisit();
                break;
            case 1:
                this.onSecondVisit();
                break;
            default:
                this.onNextVisit();
        }
    }

    private onFirstVisit() {
        this.main.delayedCall(1, () => this.onPackageArrived());
    }

    private onSecondVisit() {
        this.roomContainer.x = this.roomRight.x;
        this.showPackageButton();
        this.shelfItems.forEach(it => {
            if (it.enabled) it.visible = false;
        });
        playVO("LetsGoLuna_009", () => {
            this.shelfItems.forEach(it => {
                if (it.enabled) {
                    it.visible = true;
                    it.enabled = false;
                    it.scaleFrom(0, 0.6, Ease.backOut);
                }
            });
            playVO("LetsGoLuna_010", () => {
                this.onPackageArrived();
                this.shelfItems.forEach(it => {
                    if (it.visible) it.enabled = true;
                });
                this.takePackageIdleTimeout();
            });
        });
    }

    private onNextVisit() {
        this.roomContainer.x = this.roomRight.x;
        this.showPackageButton();
        this.onPackageArrived();
        if (this.isLastPackage) {
            playVO(["LetsGoLuna_026", "LetsGoLuna_011"], () => this.takePackageIdleTimeout());
        } else {
            this.takePackageIdleTimeout();
        }
    }

    private onActivityComplete() {
        this.normalKeyboard();
        this.showPackageButton();
        this.onPackageArrived();
    }

    private onPackageArrived() {

        const newPackage = this.gameManager.getPackage();
        this.packageBox.setModel(newPackage);

        if (this.gameManager.isFirstPackage) {
            this.door.enabled = true;
            this.door.pulse(1.05, 1.5, true);
            this.door.onPulse.add(() => this.knockKnock.show());
            Keyboard.focus(this.door);
        } else if (this.buttonPackage.visible) {
            this.buttonPackage.startTimer();
            this.buttonPackage.colorful = true;
            Keyboard.focus(this.buttonPackage);
        }
    }

    private showPackageButton() {
        if (!this.buttonPackage.visible && !this.gameManager.allPackagesReceived) {
            this.buttonPackage.visible = true;
            this.buttonPackage.moveXFrom(-this._size.x - this.buttonPackage.width * 0.5, 0.4, Ease.backOut);
        }
    }

    private openDoor(b: BaseButton, e?: PIXI.InteractionEvent) {
        if (this.debugChoosePackage(e, () => this.openDoor(b))) {
            return;
        }
        this.isDoorOpen = true;
        this.knockKnock.hide();
        this.door.stopPulse();
        this.door.enabled = false;
        this.door.open(() => this.packageBox.zIndex = this.door.zIndex + 1);
        this.packageBox.visible = true;
        this.packageBox.copyFrom(this.packageInDoor, "x", "y", "width", "height");
        this.packageBox.onClick.once(this.takePackageInDoorway, this);
        this.luna.characterSpine.autoUpdate = true;
        this.playPackageArrivedVO();
        Keyboard.focus(this.packageBox);
    }

    private onClickButtonPackage(b: BaseButton, e?: PIXI.InteractionEvent) {
        if (this.debugChoosePackage(e, () => this.onClickButtonPackage(b))) {
            return;
        }
        if (!this.buttonPackage.colorful) {
            return;
        }
        this.shelfItems.forEach(it => it.stopPulse());
        this.buttonPackage.stopPulse();
        this.buttonPackage.stopTimer();
        this.buttonPackage.colorful = false;
        this.packageBox.visible = true;
        this.packageBox.changeParent(this.main);
        this.packageBox.zIndex = this.overlay.zIndex;
        this.packageBox.copyFrom(this.packageInRoom, "x", "y", "width", "height");
        this.packageBox.onClick.once(this.takePackageOnCarpet, this);
        this.packageBox.moveXFrom(this.packageBox.x - this._size.x - this.packageBox.width, 0.8, Ease.backOut);
        this.playPackageArrivedVO();
        Keyboard.focus(this.packageBox);
    }

    private playPackageArrivedVO() {
        const isFirstPackage = this.gameManager.isFirstPackage;
        const cb = () => {
            this.isPackageTaken = false;
            this.packageBox.enabled = true;
            this.packageBox.pulse(isFirstPackage ? 1.4 : undefined, undefined, true);
            this.takePackageIdleTimeout();
        };
        if (isFirstPackage) {
            this.packageBox.enabled = false;
            playVO("LetsGoLuna_003", () => {
                this.packageBox.delayedCall(0.5, () => playVO("LetsGoLuna_004"));
                this.packageBox.delayedCall(0.6, cb);
            });
        } else {
            cb();
        }
    }

    private takePackageIdleTimeout() {
        window.clearTimeout(this.takePackageTimeout);
        this.takePackageTimeout = window.setTimeout(() => {
            playVO(this.gameManager.isFirstPackage ? ["LetsGoLuna_005", 0.5, "LetsGoLuna_006"] :
                this.gameManager.allPackagesReceived ? "LetsGoLuna_011" :
                    nextVO(packageIdleVO), {
                ...voOptionsIdle,
                callback: () => {
                    if (!this.isPackageTaken) {
                        this.takePackageIdleTimeout();
                    }
                }
            });
        }, this.gameManager.allPackagesReceived ? 10_000 : 25_000);
    }

    private takePackageInDoorway() {

        AudioChannel.SFX.play("box-in");

        this.isPackageTaken = true;
        window.clearTimeout(this.takePackageTimeout);

        const box = this.packageBox;

        box.stopPulse();
        box.enabled = false;
        box.changeParent(this.main);
        box.tweenTo(0.8, Ease.sineInOut, {
            x: this.packageInRoom.x,
            y: this.packageInRoom.y,
            width: this.packageInRoom.width,
            height: this.packageInRoom.height,
            onComplete: () => {
                playVO("LetsGoLuna_007-a", () => {
                    this.isDoorOpen = false;
                    this.door.close();
                    playVO([1, "LetsGoLuna_007-b", 0.5, "LetsGoLuna_008"], () => {
                        box.enabled = true;
                        box.pulse(undefined, undefined, true);
                        box.onClick.once(this.takePackageOnCarpet, this);
                        this.normalKeyboard();
                    });
                    this.roomContainer.moveXTo(this.roomRight.x, 3, Ease.sineInOut, () => {
                        box.changeParent(this.roomContainer);
                    });
                });
            }
        });
        this.noneKeyboard();
    }

    private takePackageOnCarpet() {

        AudioChannel.SFX.play("box-in");

        this.isPackageTaken = true;
        this.isPackageOpen = false;
        stopVO();
        window.clearTimeout(this.takePackageTimeout);
        window.clearTimeout(this.firstItemTimeout);

        const box = this.packageBox;
        const end = this.packageBox.getTransformBackup();
        const t = 0.8;

        box.stopPulse();
        box.enabled = false;
        box.changeParent(this.main);
        box.zIndex = this.overlay.zIndex + 1;
        box.tweenTo(t, Ease.sineInOut, {
            ...end,
            onComplete: () => {
                box.pulse();
                box.enabled = true;
                box.onClick.add(this.openPackage, this);
                this.startPostcardIdleInterval();
                this.packageKeyboard();
            }
        });

        const itemModel = box.getModel();
        if (itemModel) {
            const vo = FromVO(itemModel);
            if (vo) {
                playVO([...ReactionVO(), ArrayUtils.random(["LetsGoLuna_031", "LetsGoLuna_034"]), vo]);
            }
        }

        this.overlay.show(0.15, {delay: t * 0.9});
        this.noneKeyboard();
    }

    private openPackage() {
        if (this.packageBox.open()) {
            this.packageBox.stopPulse();
            AudioChannel.SFX.play("box-open");
            this.stopPostcardIdleInterval();
            this.isPackageOpen = true;
            this.packageBox.enabled = false;
            this.packageBox.moveXYTo(this.packageOpen.x, this.packageOpen.y, 0.8, Ease.sineInOut, () => {
                this.packageBox.showPostcard(() => {
                    this.showButtonGo();
                    this.lunaPrompter.show();
                });
            });
            this.noneKeyboard();
        }
    }

    private showButtonGo() {
        const buttonGo = this.buttonGo;
        buttonGo.visible = true;
        buttonGo.enabled = true;
        const buttonShownY = buttonGo.y;
        const buttonHiddenY = this._size.y + buttonGo.height * 0.5;
        buttonGo.moveYFrom(buttonHiddenY, 0.4, Ease.backOut, () => {
            buttonGo.onClick.once(() => {
                this.stopPostcardIdleInterval();
                stopVO();
                this.packageBox.hidePostcard();
                buttonGo.enabled = false;
                buttonGo.moveYTo(buttonHiddenY, 0.2, Ease.sineIn, () => {
                    buttonGo.visible = false;
                    buttonGo.y = buttonShownY;
                    this.gotoActivity();
                });
                this.noneKeyboard();
            });
        });
        this.postcardKeyboard();
        this.startPostcardIdleInterval();
    }

    private gotoActivity() {
        const newPackage = this.gameManager.getPackage();
        if (newPackage) {
            Analytics.event("level_up", this.getActivityLabel(newPackage));
            this.startActivityEvent(newPackage);
        }
        if (newPackage && newPackage.stateName === StateName.Home) {
            this.packageBox.delayedCall(0.4, () => {
                const activity = this.getActivity(newPackage);
                const button = this.getActivityItem(newPackage);
                if (activity && button) {
                    this.activityContainer.addChild(activity);
                    activity.open(this.packageBox, button, (model?: HomeScreenModel) => {
                        this.gameManager.nextPackage();
                        this.packageBox.zIndex = this.door.zIndex;
                        this.packageBox.onClick.remove();
                        this.packageBox.loadTransform();
                        this.packageBox.delayedCall(1, () => this.onActivityComplete());
                        this.endActivityEvent(model);
                    });
                }
            });
        } else if (newPackage) {
            this.lunaPrompter.hide();
            this.packageBox.delayedCall(1, () => {
                nextPackageOnStart = true;
                this.gotoState(newPackage.stateName, newPackage.itemData);
            });
        }
    }

    private getActivity(model: PackageModel): AbstractHomeActivity | undefined {
        const activityType = HomeScreenActivities[model.itemType];
        if (activityType) {
            return new activityType(this);
        }
        return undefined;
    }

    private getActivityItem(model: PackageModel): ShelfItemButton | undefined {
        return this.shelfItems.filter(it => it.itemType === model.itemType)[0];
    }

    private debugChoosePackage(e?: PIXI.InteractionEvent, cb?: Callback) {
        if (e && e.data.originalEvent.which === 3) {
            new PackageContextMenu(e, model => {
                this.gameManager.addPackage(model);
                this.packageBox.setModel(model);
                if (cb) cb();
            });
            return true;
        }
        return false;
    }

    private normalKeyboard() {
        Keyboard.clear();
        Keyboard.add(
            this.door,
            this.packageBox,
            this.buttonPackage,
            ...this.shelfItems,
            this.buttonMute,
        );
    }

    private noneKeyboard() {
        Keyboard.clear();
        Keyboard.add(this.buttonMute);
    }

    private packageKeyboard() {
        Keyboard.clear();
        Keyboard.add(
            this.packageBox,
            this.buttonMute,
        );
    }

    private postcardKeyboard() {
        Keyboard.clear();
        Keyboard.add(
            this.buttonGo,
            this.packageBox.postcard.buttonSpeaker,
            this.buttonMute,
        );
    }

    private startActivityEvent(model: PackageModel) {
        currentPackage = model;
        Analytics.event("startLevel", this.getActivityLabel(model));
    }

    private endActivityEvent(data?: HomeScreenModel) {
        if (data && !data.activityComplete) {
            if (currentPackage) {
                Analytics.event("endLevel", this.getActivityLabel(currentPackage));
            }
        }
    }

    private getActivityLabel(model: PackageModel) {
        return (model.label ?? model.itemType).toLowerCase();
    }

    private startPostcardIdleInterval() {
        let index = 0;
        window.clearInterval(this.postcardIdleInterval);
        this.postcardIdleInterval = window.setInterval(() => {
            playVO(this.isPackageOpen ? "LetsGoLuna_021" : ["LetsGoLuna_020", "LetsGoLuna_008"][index % 2], voOptionsIdle);
            index++;
        }, 15_000);
    }

    private stopPostcardIdleInterval() {
        window.clearInterval(this.postcardIdleInterval);
    }
}
